import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import SkipLink from './SkipLink';
import Footer from './Footer';
import Icon from './Icon';
import Seo, { SITE } from './Seo';
import { trackPageview, trackEvent } from '../utils/analytics';
import { PHONE } from '../content/contacts';
import { tie } from './page/typography';

/**
 * Спільна обгортка внутрішніх сторінок.
 *
 * Шапка тут завжди світла (solid): на головній вона прозора, бо лежить поверх
 * відео, а тут під нею звичайний фон — білі літери на білому не читалися б.
 *
 * Меню всюди однакове: кожен пункт — це адреса, тож на внутрішній сторінці
 * воно поводиться так само, як на головній.
 *
 * Ширина одна для всіх сторінок сайту — тієї самої, що й у секцій головної.
 * Довгі рядки на сторінках суцільного тексту стримуються не вужчим
 * контейнером, а обмеженням самих абзаців: інакше в сторінки послуги був би
 * свій лівий край, відмінний від решти сайту.
 *
 * `banner` — широке фото над заголовком. Беремо постери hero-відео, які вже
 * лежать на сервері: нові файли означали б ще пів мегабайта на кожну сторінку
 * заради того самого ефекту.
 *
 * `cta` — заклик наприкінці сторінки:
 * - не передано — універсальний градієнтний банер;
 * - { title, text } — свої слова: на сторінці ліцензування доречніше
 *   запропонувати перевірку документів до подання, ніж «обговорімо вашу
 *   ситуацію»;
 * - { variant: 'light' } — світла панель замість градієнта, коли сторінка
 *   вже має свій кольоровий банер і два однакові поспіль злилися б у
 *   рекламу;
 * - { source } — мітка для статистики кліків (за замовчуванням page-shell);
 * - false — без заклику: коли сторінка закінчується власним.
 */
const DEFAULT_CTA = {
  title: 'Обговоримо вашу ситуацію',
  text: 'Розкажіть, на якому ви етапі — і ми скажемо, що знадобиться саме вам, у які строки та за яких умов.'
};

const PageShell = ({
  title, description, path, schema, crumbs = [], heading, lead,
  banner, noindex, cta, children
}) => {
  useEffect(() => {
    trackPageview();
  }, [path]);

  const trail = [{ name: 'Головна', to: '/' }, ...crumbs];
  const container = 'page-container';

  // schema приймає і один вузол, і масив: сторінка послуги віддає одразу
  // Service та FAQPage, і обидва мають лежати в @graph поруч, а не вкладено.
  const extraSchema = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: SITE + (c.to || path)
    }))
  };

  const breadcrumbs = (light) => (
    <nav aria-label="Навігація по сайту" className={`mb-5 text-sm ${light ? 'text-white/70' : 'text-slate-500'}`}>
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {trail.map((c, i) => (
          <li key={c.name} className="flex items-center gap-x-2">
            {i > 0 && <span aria-hidden="true" className={light ? 'text-white/40' : 'text-slate-300'}>/</span>}
            {i === trail.length - 1 ? (
              <span className={light ? 'text-white' : 'text-slate-700'}>{c.name}</span>
            ) : (
              // py-3 -my-3: рядок крихт не росте, а ціль для пальця — 44px.
              <Link to={c.to} className={`inline-block py-3 -my-3 transition-colors duration-200 ${light ? 'hover:text-white' : 'hover:text-teal-700'}`}>
                {c.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );

  const headingBlock = (light) => (
    <>
      {/* Між 1024 і 1536 розмір не змінювався зовсім, тож на моніторі 1920
          заголовок сторінки виглядав дрібнішим за заголовки її ж підрозділів.

          Інтерліньяж задано при кожному кеглі (text-…/[1.1]), а не одним
          leading-[1.12]: sm:text-4xl приносить свій line-height 2.5rem, а
          правило з медіазапитом стоїть у CSS пізніше й перебиває загальне.
          На 1280–1535 кегль 46px ішов із рядком 40px, і двохрядковий
          заголовок («Юристи, для яких медичне право…») налазив рядок на
          рядок. text-balance ділить такий заголовок на рівні рядки замість
          одного слова внизу. */}
      <h1 className={`text-3xl/[1.15] sm:text-4xl/[1.12] lg:text-[2.5rem]/[1.1] xl:text-[2.9rem]/[1.1] 2xl:text-5xl/[1.1] font-black font-display text-balance ${light ? 'text-white' : 'text-slate-900'}`}>
        {heading}
      </h1>
      <div className="w-16 h-1 bg-gradient-to-r from-teal-400 to-sky-400 mt-5 mb-5"></div>
      {lead && (
        <p className={`max-w-2xl text-base lg:text-lg leading-relaxed ${light ? 'text-slate-200' : 'text-slate-600'}`}>
          {lead}
        </p>
      )}
    </>
  );

  // overflow-x-clip, а не hidden: hidden робить обгортку контейнером
  // прокрутки, і position: sticky усередині (зміст статті, розділи запитань)
  // прив'язувався до неї, а не до вікна — тобто не липнув. clip так само
  // ховає горизонтальне переповнення, але прокрутки не створює. Safari до 16
  // clip не знає — там лишається hidden.
  return (
    <div className="App bg-white text-slate-800 min-h-screen flex flex-col overflow-x-hidden supports-[overflow:clip]:overflow-x-clip">
      <Seo
        title={title}
        description={description}
        path={path}
        schema={{ '@context': 'https://schema.org', '@graph': [...extraSchema, breadcrumbSchema] }}
        noindex={noindex}
      />

      <SkipLink />

      <Navbar solid initialTone={banner ? 'dark' : 'light'} />

      <main id="content" tabIndex={-1} className="flex-1 focus:outline-none">
        {banner ? (
          <header className="relative overflow-hidden bg-slate-900" data-tone="dark">
            {/*
              alt порожній навмисно: фото декоративне й нічого не додає до
              змісту, а озвучувати його читачам з екранного диктора — тільки
              заважати. Заголовок нижче каже все потрібне.
            */}
            {/*
              object-position зсунуто вгору. Банер широкий і невисокий
              (приблизно 3.7:1), фото — 16:10, тож object-cover лишає від
              кадру горизонтальну смугу. За замовчуванням вона береться
              з середини — і в половини знімків обличчя опинялося рівно на
              межі зрізу. 38% від верху лишає обличчя в кадрі й не оголює
              стелю кабінету.

              Файл віддається у двох розмірах: на телефоні банер вужчий за
              800px, і качати 1600px там немає сенсу.
            */}
            <img
              src={process.env.PUBLIC_URL + banner.replace(/\.jpg$/, '-sm.jpg')}
              srcSet={`${process.env.PUBLIC_URL + banner.replace(/\.jpg$/, '-sm.jpg')} 800w, ${process.env.PUBLIC_URL + banner} 1600w`}
              sizes="100vw"
              alt=""
              aria-hidden="true"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-[center_38%]"
            />
            <div className="absolute inset-0 bg-slate-900/75"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/30 to-slate-900/60"></div>
            <div className={`${container} relative pt-28 lg:pt-36 pb-12 lg:pb-16`}>
              {breadcrumbs(true)}
              {headingBlock(true)}
            </div>
          </header>
        ) : (
          <div className={`${container} pt-28 lg:pt-32 pb-10 lg:pb-14`}>
            {breadcrumbs(false)}
            {headingBlock(false)}
          </div>
        )}

        {children}

        {/*
          Заклик наприкінці кожної сторінки.

          Раніше кнопка вела на «/#contact» — тобто скидала людину на початок
          головної і прокручувала її повз увесь сайт до форми. Тепер вона веде
          на /kontakty: там та сама форма, але зі своїм заголовком, прямими
          контактами й поясненням, що буде після звернення.

          На самій сторінці контактів цей блок кликав би туди, де людина вже
          стоїть, тож там його немає — форма й прямі контакти вище.
        */}
        {path !== '/kontakty' && cta !== false && <FinalCta cta={cta} />}
      </main>

      <Footer />
    </div>
  );
};

/**
 * Заклик у два стовпці: текст ліворуч, дії праворуч. Раніше все стояло в
 * лівій половині — заголовок, абзац і кнопки під ним, — а права половина
 * смуги лишалася порожньою. Нижче lg порядок той самий, просто в один
 * стовпець.
 *
 * Поруч із кнопкою — телефон: частина людей дочитує сторінку саме тому, що
 * хоче поставити одне питання голосом, і змушувати їх заповнювати анкету
 * заради цього немає сенсу.
 */
const FinalCta = ({ cta }) => {
  const { title, text, variant, source = 'page-shell' } = cta || {};
  const light = variant === 'light';
  return (
    <div className="page-container pb-16 lg:pb-24">
      <div
        className={
          light
            ? 'relative rounded-2xl border border-slate-200 bg-slate-50 p-6 lg:p-10'
            : 'relative bg-gradient-to-r from-teal-700 to-sky-700 rounded-2xl p-6 lg:p-10 text-white shadow-md overflow-hidden'
        }
      >
        {!light && (
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        )}
        <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-12 lg:items-center">
          <div>
            <h2 className={`text-2xl lg:text-3xl font-black font-display mb-3 text-balance ${light ? 'text-slate-900' : ''}`}>
              {tie(title || DEFAULT_CTA.title)}
            </h2>
            <p className={`text-base lg:text-lg leading-relaxed max-w-2xl ${light ? 'text-slate-600' : 'opacity-95'}`}>
              {tie(text || DEFAULT_CTA.text)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:gap-4">
            <Link
              to="/kontakty"
              onClick={() => trackEvent('cta_click', source)}
              className={`inline-flex items-center justify-center gap-3 font-bold px-6 py-3 rounded-lg transition-colors duration-300 whitespace-nowrap ${
                light ? 'bg-teal-700 text-white hover:bg-teal-800' : 'bg-white text-teal-700 hover:bg-teal-50'
              }`}
            >
              Отримати консультацію
              <Icon name="fa-arrow-right" />
            </Link>
            <a
              href={PHONE.href}
              onClick={() => trackEvent('contact_channel', `${source}-phone`)}
              className={`inline-flex items-center justify-center gap-3 font-bold px-6 py-3 rounded-lg transition-colors duration-300 whitespace-nowrap ${
                light
                  ? 'border border-slate-300 bg-white text-slate-800 hover:border-teal-600 hover:text-teal-800'
                  : 'border border-white/60 text-white hover:bg-white/10'
              }`}
            >
              <Icon name="fa-phone" className={light ? 'text-teal-700' : ''} />
              {PHONE.label}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageShell;
